import React, {useEffect, Fragment} from 'react';
import {Flex, Group, Popup, FormField, Dropdown, Button, Icons, Checkbox, List, Form} from 'components';
import {notify, over, reject, find, includes, useNamedSelector, useInterval, useEvent, usePopup} from 'helpers';
import {signIn, signOut, fetchCalendars, toggleCalendar, deleteImportedEvents} from 'store/google-calendar';
import {forbid} from 'store/session';
import {set} from 'store/settings';

export function CalendarSync() {
  const {user, ready, calendars, defaultCalendar, importCalendars} = useNamedSelector('googleCalendar.settings');
  const {premium} = useNamedSelector('session.subscription');

  useEvent('focus', fetchCalendars);
  useEffect(fetchCalendars, []);
  useInterval(fetchCalendars, 30000);

  return (
    <div>
      <h1>Google Calendar Sync</h1>
      { user ?
        <Form initialValues={{ defaultCalendar }} className="medium-spacing" onChange={({ defaultCalendar }) => set({ module: 'googleCalendar', key: 'defaultCalendar', value: defaultCalendar })}>
          <List tiny-gutter>
            <h6>Export activities</h6>
            <Dropdown label={defaultCalendar ? 'Calendar' : 'Select calendar..'} name="defaultCalendar" Popup={ExportCalendars}>
              { value => find(calendars, { id: defaultCalendar }) &&
                <b>{ value == 'primary' ? 'Primary' : find(calendars, { id: defaultCalendar }).summary }</b>
              }
            </Dropdown>
            <p className="secondary small">
              All new activities and routines will be exported to the selected calendar.
            </p>
            <h6>Import events</h6>
            <Group>
              { calendars && calendars.map(calendar =>
                <Checkbox.Container
                  key={calendar.id}
                  checked={importCalendars.includes(calendar.id)}
                  onClick={over(() => toggleCalendar(calendar.id))}
                  className="has-background"
                  children={calendar.primary ? 'Primary' : calendar.summary}
                />
              ) }
            </Group>
            <h6>Signed in as</h6>
            <FormField>
              <b>{ user.email }</b>
              <b className="primary link" onClick={signOut}>Sign out</b>
            </FormField>
            <p className="secondary small">
              Please note that if you use the app on other devices, you need to enable 'Google Calendar Sync' on each device.
            </p>
          </List>
        </Form>
        : <Form className="medium-spacing" onSubmit={ready && (premium ? signIn : forbid)}>
          { form =>
            <List tiny-gutter>
              <p>To enable Syncing activities/events with your Google Calendar, please login to your Google account.<br /><br /></p>
              <FormField>
                <button type="submit" muted className="primary link">
                  { ready ? 'Sign In' : 'Loading..' }
                </button>
                { (form.isSubmitting || !ready) &&
                  <Icons.Spinner />
                }
              </FormField>
              <p className="secondary small">
                Please note that if you use the app on other devices, you need to enable 'Google Calendar Sync' on each device.
              </p>
            </List>
          }
        </Form>
      }
    </div>
  );
}

export function DeleteImportedEventsPopup({ id, popup }) {
  return (
    <Fragment>
      { !id &&
        <h2>Delete</h2>
      }
      <p>Do you want to delete all previously imported Google Calendar events?</p>
      <Flex className={['spacing', 'right', 'tiny-gutter']}>
        <Button className="borderless" onClick={popup.close}>No</Button>
        <Button className="danger" onClick={over(() => deleteImportedEvents({ id }), popup.close)}>Delete</Button>
      </Flex>
    </Fragment>
  );
}

function ExportCalendars({ value, setValue }) {
  const { calendars } = useNamedSelector('googleCalendar.settings');
  return (
    <Popup.Menu>
      { reject(calendars, { accessRole: 'reader' }).map(calendar =>
        <Popup.Menu.Item key={calendar.id} pressed={calendar.id == value} onClick={() => setValue(calendar.id)}>
          { calendar.primary ? 'Primary' : calendar.summary }
        </Popup.Menu.Item>
      ) }
      <Popup.Menu.Item pressed={!value} onClick={() => setValue(null)}>
        Do not export
      </Popup.Menu.Item>
    </Popup.Menu>
  );
}

export function CalendarSyncPopup({ popup }) {
  return (
    <Fragment>
      <h2>Google Calendar Sync</h2>
      <p>Google Calendar Synchronization is enabled on one of your devices. Do you want to enable it on this device too?</p>
      <p><Button className="primary" to="/settings/calendar-sync/" onClick={popup.close}>Go to settings</Button></p>
    </Fragment>
  );
}
